import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import { AvailabilityMonthGrid } from '@apps/web/components/caregivers/caregiver_availability_month_grid';
import {
    buildTimeboxedDayGroups,
    mergeTimeboxedAvailability,
    type TimelineItem,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_model';
import { CaregiverSchedulingProfile } from '@apps/web/components/caregivers/caregiver_scheduling_profile';
import { formatHoursWanted } from '@apps/web/components/caregivers/caregiver_scheduling_profile_format';
import { ProfileNotesPanel } from '@apps/web/components/entity_profile/profile_notes_panel';
import { Button } from '@apps/web/components/ui/button';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    addMonthsToDateKey,
    endOfMonthDateKey,
    formatMonthKey,
    startOfMonthDateKey,
    toDateKey,
} from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import {
    useCaregiverAvailabilityProfile,
    useCaregiverSchedulingProfile,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

type CaregiverOverviewProps = {
    readonly organizationId: string;
    readonly caregiverId: string;
    readonly caregiverFirstName: string | null;
    readonly timezone: string | null;
    readonly externalSourceLabel?: string;
    readonly schedulingProfilesEnabled: boolean;
};

/**
 * Landing section of the caregiver popup: one month grid merging
 * availability windows and assigned shifts, with scheduling preferences
 * and notes beside it — the "can they take this shift?" view.
 */
export function CaregiverOverview({
    organizationId,
    caregiverId,
    caregiverFirstName,
    timezone,
    externalSourceLabel,
    schedulingProfilesEnabled,
}: CaregiverOverviewProps) {
    const todayKey = useMemo(
        () => (timezone ? toDateKey(new Date(), timezone) : null),
        [timezone],
    );
    // Null until the user navigates; the visible month then follows today.
    const [anchorOverrideKey, setAnchorOverrideKey] = useState<string | null>(
        null,
    );
    const anchorDateKey = anchorOverrideKey ?? todayKey;
    const monthStartKey = anchorDateKey
        ? startOfMonthDateKey(anchorDateKey)
        : null;
    const monthEndKey = monthStartKey ? endOfMonthDateKey(monthStartKey) : null;

    const { data, error, isLoading } = useCaregiverAvailabilityProfile(
        organizationId,
        caregiverId,
        monthStartKey,
        monthEndKey,
    );
    const { data: schedulingProfile } = useCaregiverSchedulingProfile(
        organizationId,
        schedulingProfilesEnabled ? caregiverId : null,
    );

    const dayGroups = useMemo(() => {
        if (!data || !timezone) {
            return [];
        }
        const items: Array<TimelineItem> = [
            ...mergeTimeboxedAvailability(
                data.availability,
                externalSourceLabel,
            ),
        ];
        data.shifts.forEach((shift) => {
            items.push({
                kind: 'shift',
                id: `shift-${shift.id}`,
                type: 'shift',
                startTime: shift.shift_start_time,
                endTime: shift.shift_end_time,
                details: shift.patient_name,
            });
        });
        return buildTimeboxedDayGroups(items, timezone);
    }, [data, externalSourceLabel, timezone]);

    // Scheduled time covers the visible month; the preference is explicitly
    // weekly. Keep both units visible so the comparison cannot be mistaken for
    // like-for-like values.
    const workloadLabel = useMemo(() => {
        if (!data) {
            return null;
        }
        const scheduledMs = data.shifts.reduce(
            (total, shift) =>
                total +
                (new Date(shift.shift_end_time).getTime() -
                    new Date(shift.shift_start_time).getTime()),
            0,
        );
        const scheduledHours = Math.round(scheduledMs / 3_600_000);
        const hoursWanted = schedulingProfile?.hours_wanted
            ? formatHoursWanted(String(schedulingProfile.hours_wanted))
            : null;
        return hoursWanted
            ? `${scheduledHours} hrs this month · wants ${hoursWanted}`
            : `${scheduledHours} hrs this month`;
    }, [data, schedulingProfile?.hours_wanted]);

    if (!timezone || !monthStartKey || !todayKey) {
        return (
            <div className="flex h-40 items-center justify-center">
                <Spinner className="size-5" />
            </div>
        );
    }

    return (
        <div className="min-w-0">
            <h2 className="mb-3 flex min-h-8 items-center text-base font-semibold">
                Overview
            </h2>
            <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1.55fr)_minmax(280px,0.75fr)]">
                <div className="flex min-w-0 flex-col gap-2">
                    <div className="flex items-center gap-0.5">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="size-6"
                            aria-label="Previous month"
                            onClick={() =>
                                setAnchorOverrideKey(
                                    addMonthsToDateKey(monthStartKey, -1),
                                )
                            }
                        >
                            <ChevronLeft className="size-3.5" />
                        </Button>
                        <span className="text-xs font-medium">
                            {formatMonthKey(monthStartKey, timezone)}
                        </span>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="size-6"
                            aria-label="Next month"
                            onClick={() =>
                                setAnchorOverrideKey(
                                    addMonthsToDateKey(monthStartKey, 1),
                                )
                            }
                        >
                            <ChevronRight className="size-3.5" />
                        </Button>
                        <span
                            data-testid="caregiver-workload"
                            className={cn(
                                'text-muted-foreground ml-auto text-xs tabular-nums',
                                !workloadLabel && 'hidden',
                            )}
                        >
                            {workloadLabel}
                        </span>
                        {isLoading && <Spinner className="size-4" />}
                    </div>
                    {error ? (
                        <div
                            className="text-destructive flex min-h-40 items-center justify-center text-sm"
                            role="alert"
                        >
                            Failed to load caregiver schedule.
                        </div>
                    ) : (
                        <AvailabilityMonthGrid
                            monthStartKey={monthStartKey}
                            todayKey={todayKey}
                            timezone={timezone}
                            dayGroups={dayGroups}
                        />
                    )}
                </div>
                <aside className="flex min-w-0 flex-col">
                    {schedulingProfilesEnabled && (
                        <div className="border-b pb-4">
                            <CaregiverSchedulingProfile
                                organizationId={organizationId}
                                caregiverId={caregiverId}
                            />
                        </div>
                    )}
                    <div
                        className={cn(
                            schedulingProfilesEnabled ? 'pt-4' : undefined,
                        )}
                    >
                        <ProfileNotesPanel
                            organizationId={organizationId}
                            entityType="caregiver"
                            entityId={caregiverId}
                            entityFirstName={caregiverFirstName}
                        />
                    </div>
                </aside>
            </div>
        </div>
    );
}
